// Flushes queued offline field submissions: upload photos -> pairs -> field capture.
// Resumable per item (serverId/done flags persisted) so retries never duplicate uploads.
import api from "@/lib/api";
import { allItems, updateItem, removeItem } from "@/lib/offlineQueue";

export async function flushOne(item) {
  const idMap = {};
  for (const ph of item.photos) {
    if (ph.serverId) { idMap[ph.localId] = ph.serverId; continue; }
    const { data } = await api.post("/photos", {
      work_order_id: item.work_order_id,
      data_url: ph.data_url,
      caption: ph.caption || null,
      lat: ph.lat ?? null,
      lng: ph.lng ?? null,
      annotated: !!ph.annotated,
      original_data_url: ph.annotated ? ph.original_data_url || null : null,
      quality: ph.quality || null,
    }, { timeout: 60000 });
    ph.serverId = data.id;
    idMap[ph.localId] = data.id;
    await updateItem(item);
  }
  for (const pr of item.pairs || []) {
    if (pr.done) continue;
    const before = idMap[pr.beforeLocalId];
    const after = idMap[pr.afterLocalId];
    if (before && after && before !== after) {
      try {
        await api.post("/photo-pairs", { work_order_id: item.work_order_id, before_photo_id: before, after_photo_id: after, caption: pr.caption || null });
      } catch { /* pair is best-effort */ }
    }
    pr.done = true;
    await updateItem(item);
  }
  await api.post(`/work-orders/${item.work_order_id}/field-capture`, {
    safety_confirmed: true,
    safety_notes: item.payload.safety_notes,
    quantities: item.payload.quantities,
    labour: item.payload.labour,
    photo_ids: item.photos.map((p) => p.serverId).filter(Boolean),
    notes: item.payload.notes,
  }, { timeout: 60000 });
  await removeItem(item.id);
}

export async function flushQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { flushed: 0, pending: (await allItems()).length };
  }
  const items = await allItems();
  let flushed = 0;
  for (const item of items) {
    try { await flushOne(item); flushed++; } catch { /* leave in queue for next retry */ }
  }
  return { flushed, pending: (await allItems()).length };
}

export async function pendingCount() {
  return (await allItems()).length;
}
